/**
 * SortablePhotoGrid
 *
 * Wraps a photo tile grid with drag-to-reorder capability via @dnd-kit.
 *
 * The child render function receives `dragHandleProps` — spread those on a
 * small grip icon inside each tile so the drag only fires from the handle
 * (not from tapping an input, button, or the photo itself). Without a
 * dedicated handle, users trying to type in caption inputs would trigger
 * drags every time they pressed down.
 *
 * Items must have a stable string `id`; @dnd-kit needs identifiable rows
 * to compute the move. For pending photos we use their in-memory id; for
 * uploaded photos we use the photo URL (unique per storage path).
 */

import React from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortablePhotoGridProps<T> {
  items: T[];
  onReorder: (next: T[]) => void;
  /** Extract a stable ID for each item. Defaults to reading `.id` off the
   *  item — works for pending photos which already have an in-memory id.
   *  For already-uploaded photos (which have `url` but no `id`), pass
   *  `(p) => p.url` since URLs are unique per storage path. */
  getId?: (item: T) => string;
  className?: string;
  children: (
    item: T,
    index: number,
    dragHandleProps: React.HTMLAttributes<HTMLElement>,
  ) => React.ReactNode;
}

export function SortablePhotoGrid<T>({
  items,
  onReorder,
  getId = (item: T) => (item as { id: string }).id,
  className,
  children,
}: SortablePhotoGridProps<T>) {
  const sensors = useSensors(
    // 5px activation constraint so a click on a button inside the tile
    // doesn't get eaten as a drag start.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => getId(i) === active.id);
    const newIndex = items.findIndex((i) => getId(i) === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext
        items={items.map(getId)}
        strategy={rectSortingStrategy}
      >
        <div className={className}>
          {items.map((item, index) => {
            const id = getId(item);
            return (
              <SortableTile key={id} id={id}>
                {(dragHandleProps) => children(item, index, dragHandleProps)}
              </SortableTile>
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableTile({
  id,
  children,
}: {
  id: string;
  children: (
    dragHandleProps: React.HTMLAttributes<HTMLElement>,
  ) => React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        // Ensure the dragged tile floats above its neighbors while moving.
        zIndex: isDragging ? 10 : undefined,
      }}
      {...attributes}
    >
      {children({ ...listeners } as React.HTMLAttributes<HTMLElement>)}
    </div>
  );
}
